// Detects lessons that must play from the video platform (R2/HLS) versus the
// legacy Vimeo/YouTube path. Ported from profile-new's `src/lib/videoSource.js`
// but WITHOUT the parseVimeoUrl dependency — here every non-R2 URL simply falls
// through to the existing hardcoded provider behaviour in the dispatcher.
//
// Two R2 shapes are supported:
//   1) Canonical LMS watch URL → BFF / video-service play link (HLS/mp4).
//   2) Direct signed HTTPS from course API (`provider=r2`, r2:// → temporaryUrl)
//      → play the signed mp4 as-is (no watch/ BFF).

// Canonical LMS watch URL — an identity convention shared with admin-new's
// «Скопіювати посилання для LMS». Deliberately a literal (never env config):
// URLs pasted into the LMS DB must keep working even if API hosts move.
export const R2_WATCH_BASE = "https://video.genius.space/watch/";

// Derived from R2_WATCH_BASE (rather than re-typed) so the canonical URL has
// exactly one source of truth. Anchored ^…$ on the trimmed string so a spoof
// host such as `https://evil.com/video.genius.space/watch/1` can never match.
const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const WATCH_RE = new RegExp(`^${escapeRegExp(R2_WATCH_BASE)}(\\d+)/?$`);

/**
 * Course-service signed playback URLs (S3-compatible R2 temporaryUrl).
 * @param {*} url
 * @returns {boolean}
 */
export function isDirectR2PlaybackUrl(url) {
    if (!url) return false;
    const raw = String(url).trim();
    if (!/^https:\/\//i.test(raw)) return false;
    // Never treat the canonical watch URL as a "direct" object URL.
    if (WATCH_RE.test(raw)) return false;
    if (/r2\.cloudflarestorage\.com/i.test(raw)) return true;
    if (/\.r2\.dev(?:\/|\?|$)/i.test(raw)) return true;
    return false;
}

/**
 * Classify a lesson video URL coming from the LMS / course API.
 * @param {*} url
 * @returns {{kind:'r2', lessonId:number} | {kind:'r2-direct', src:string} | {kind:'other'}}
 */
export function parseVideoSource(url) {
    if (!url) return { kind: "other" };
    const raw = String(url).trim();
    const match = raw.match(WATCH_RE);
    if (match) return { kind: "r2", lessonId: Number(match[1]) };
    if (isDirectR2PlaybackUrl(raw)) return { kind: "r2-direct", src: raw };
    return { kind: "other" };
}

/**
 * Provider decision used by the dispatcher (`video-providers/index.vue`):
 * R2 watch URL, course `provider=r2`, or a direct signed R2 URL → r2 provider;
 * everything else keeps the current byte-for-byte legacy (Vimeo/YouTube) behaviour.
 * @param {{src?:*, provider?:*}} videoInfo
 * @returns {'r2' | 'legacy'}
 */
export function chooseProvider(videoInfo) {
    if (videoInfo && String(videoInfo.provider || "").toLowerCase() === "r2") {
        return "r2";
    }
    const src = videoInfo && videoInfo.src;
    const parsed = parseVideoSource(src);
    return parsed.kind === "r2" || parsed.kind === "r2-direct" ? "r2" : "legacy";
}

/**
 * Dispatcher decision for the legacy `<video-player>` (lesson pages,
 * materials, action-plans). Its historical substring checks are preserved
 * byte-for-byte — R2 detection just runs first, so a canonical watch URL or a
 * course signed R2 mp4 no longer falls into "unknown" (which the template
 * renders as nothing).
 * @param {*} url
 * @returns {'r2' | 'vimeo' | 'youtube' | 'unknown'}
 */
export function videoPlayerType(url) {
    const parsed = parseVideoSource(url);
    if (parsed.kind === "r2" || parsed.kind === "r2-direct") return "r2";
    if (!url) return "unknown";
    const raw = String(url);
    return raw.includes("vimeo") ? "vimeo" : raw.includes("youtu") ? "youtube" : "unknown";
}
